<template>
  <form @submit.prevent="handleSubmit">
    <base-input
      v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)"
      :label="`${$t('COMMON.ORGANIZATION')} (*)`"
      :placeholder="$t('COMMON.ORGANIZATION')"
    >
      <organization-selector
        :allowNone="true"
        :organization="order.organization.id"
        :filterable="true"
        :showAll="false"
        :disabled="!!order.id"
        @organizationChanged="onOrganizationChanged"
      />
    </base-input>
    <validation-error :errors="apiValidationErrors.organization" />

    <base-input
      :label="`${$t('ESTABLISHMENT_INTERNAL_ORDERS.SATELLITE_ESTABLISHMENT')} (*)`"
      :placeholder="$t('ESTABLISHMENT_INTERNAL_ORDERS.SATELLITE_ESTABLISHMENT')"
      v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)"
    >
      <establishment-selector
        :onlyActive="true"
        :disabled="!!order.id"
        :establishment="
          order.destinationEstablishment ? order.destinationEstablishment.id : null
        "
        :filterable="true"
        :showAll="false"
        :filterOrganization="order.organization.id"
        @establishmentChanged="onDestinationEstablishmentChanged"
      />
    </base-input>
    <validation-error :errors="apiValidationErrors.destinationEstablishment" />

    <!-- Non-blocking: an establishment without a SATELLITE cafeteria is not supposed
         to order, but we only warn — no server-side check backs this rule. -->
    <div class="alert alert-warning" v-if="satelliteWarning">
      <i class="fas fa-exclamation-triangle mr-1"></i>
      {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.NO_SATELLITE_CAFETERIA_WARNING") }}
    </div>

    <base-input
      :label="`${$t('ESTABLISHMENT_INTERNAL_ORDERS.PRODUCTION_ESTABLISHMENT')} (*)`"
      :placeholder="$t('ESTABLISHMENT_INTERNAL_ORDERS.PRODUCTION_ESTABLISHMENT')"
      v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)"
    >
      <production-establishment-selector
        :onlyActive="true"
        :disabled="!!order.id"
        :establishment="order.sourceEstablishment ? order.sourceEstablishment.id : null"
        :filterable="true"
        :filterOrganization="order.organization.id"
        @establishmentChanged="onSourceEstablishmentChanged"
        @loaded="onProductionEstablishmentsLoaded"
      />
    </base-input>
    <validation-error :errors="apiValidationErrors.sourceEstablishment" />

    <div class="alert alert-warning" v-if="noProductionEstablishment">
      <i class="fas fa-exclamation-triangle mr-1"></i>
      {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.NO_PRODUCTION_ESTABLISHMENT_WARNING") }}
    </div>

    <div class="alert alert-info" v-if="productionResolvedFromCafeteria">
      <i class="fas fa-info-circle mr-1"></i>
      {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.PRODUCTION_CAFETERIA") }} :
      {{ productionResolvedFromCafeteria }}
    </div>

    <base-input
      :label="`${$t('ESTABLISHMENT_INTERNAL_ORDERS.WEEK_STARTS_AT')} (*)`"
      :placeholder="$t('ESTABLISHMENT_INTERNAL_ORDERS.WEEK_STARTS_AT')"
    >
      <flat-picker
        v-model="order.week_starts_at"
        :config="{
          allowInput: true,
          locale: $flatPickrLocale(),
        }"
        class="form-control datepicker"
        @on-change="onWeekChanged"
      />
    </base-input>
    <validation-error :errors="apiValidationErrors.week_starts_at" />

    <base-input
      :label="$t('ESTABLISHMENT_INTERNAL_ORDERS.WEEK_ENDS_AT')"
      :placeholder="$t('ESTABLISHMENT_INTERNAL_ORDERS.WEEK_ENDS_AT')"
    >
      <flat-picker
        v-model="order.week_ends_at"
        :config="{
          allowInput: true,
          locale: $flatPickrLocale(),
        }"
        class="form-control datepicker"
        @change="onFormChanged()"
      />
    </base-input>
    <validation-error :errors="apiValidationErrors.week_ends_at" />

    <h2 class="mt-4">{{ $t("COMMON.NOTE") }}</h2>
    <html-editor v-model="order.excerpt" @change="onFormChanged()" />
    <validation-error :errors="apiValidationErrors.excerpt" />

    <establishment-inactive-alert :establishment="selectedSourceEstablishment" />
    <establishment-inactive-alert :establishment="selectedDestinationEstablishment" />

    <div class="text-right mt-4">
      <base-button
        v-if="!isSelectedEstablishmentInactive"
        type="primary"
        native-type="submit"
        :loading="loading"
        :disabled="loading"
      >
        {{ submitLabel || $t("COMMON.SAVE") }}
      </base-button>
    </div>
  </form>
</template>

<script>
import cloneDeep from "lodash/cloneDeep";
import moment from "moment";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import OrganizationSelector from "@/components/OrganizationSelector.vue";
import EstablishmentSelector from "@/components/EstablishmentSelector.vue";
import ProductionEstablishmentSelector from "@/components/ProductionEstablishmentSelector.vue";
import {
  establishmentCanOrder,
  findSatelliteCafeteriaWithProduction,
} from "@/helpers/internalOrderEligibility";
import EstablishmentInactiveAlert from "@/views/Pages/CafeteriaModule/EstablishmentManagement/partials/EstablishmentInactiveAlert.vue";
import HtmlEditor from "@/components/Inputs/HtmlEditor.vue";
import ValidationError from "@/components/ValidationError.vue";

export default {
  name: "EstablishmentInternalOrderForm",
  components: {
    OrganizationSelector,
    EstablishmentSelector,
    ProductionEstablishmentSelector,
    EstablishmentInactiveAlert,
    HtmlEditor,
    ValidationError,
    flatPicker,
  },
  props: {
    orderModel: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    submitLabel: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      order: cloneDeep(this.orderModel),
      apiValidationErrors: {},
      selectedSourceEstablishment: null,
      selectedDestinationEstablishment: null,
      productionResolvedFromCafeteria: null,
      satelliteWarning: false,
      noProductionEstablishment: false,
    };
  },
  computed: {
    isSelectedEstablishmentInactive() {
      return (
        !!(
          this.selectedSourceEstablishment &&
          this.selectedSourceEstablishment.active === false
        ) ||
        !!(
          this.selectedDestinationEstablishment &&
          this.selectedDestinationEstablishment.active === false
        )
      );
    },
  },
  created() {
    this.ensureRelationObjects();
  },
  watch: {
    orderModel: {
      deep: true,
      handler(value) {
        this.order = cloneDeep(value);
        this.ensureRelationObjects();
      },
    },
  },
  methods: {
    ensureRelationObjects() {
      this.order.organization = this.order.organization || {
        type: "organizations",
        id: null,
      };
      this.order.sourceEstablishment = this.order.sourceEstablishment || {
        type: "establishments",
        id: null,
      };
      this.order.destinationEstablishment = this.order.destinationEstablishment || {
        type: "establishments",
        id: null,
      };
      if (!this.order.id && !this.order.week_starts_at) {
        this.order.week_starts_at = moment()
          .add(1, "week")
          .startOf("isoWeek")
          .format("YYYY-MM-DD");
      }
      this.alignWeekEnd();
      this.order = this.$fillUserOrganizationData(this.order);
    },
    /**
     * The ordered week always starts on a monday and ends on the friday: keep the
     * two dates consistent whatever the user picks.
     */
    alignWeekEnd() {
      if (!this.order.week_starts_at) return;
      const monday = moment(this.order.week_starts_at).startOf("isoWeek");
      this.order.week_starts_at = monday.format("YYYY-MM-DD");
      this.order.week_ends_at = monday.clone().add(4, "days").format("YYYY-MM-DD");
    },
    onWeekChanged() {
      this.alignWeekEnd();
      this.onFormChanged();
    },
    onOrganizationChanged(organizationId) {
      if (this.order.id) return;
      this.order.organization.id = organizationId;
      this.order.sourceEstablishment.id = null;
      this.order.destinationEstablishment.id = null;
      this.onFormChanged();
    },
    onSourceEstablishmentChanged(establishmentId, establishment) {
      this.selectedSourceEstablishment = establishment || null;
      if (this.order.id) return;
      this.order.sourceEstablishment.id = establishmentId;
      this.onFormChanged();
    },
    async onDestinationEstablishmentChanged(establishmentId, establishment) {
      this.selectedDestinationEstablishment = establishment || null;
      if (this.order.id) return;
      this.order.destinationEstablishment.id = establishmentId;
      await this.checkSatelliteEligibility(establishmentId);
      await this.resolveProductionEstablishment(establishmentId);
      this.onFormChanged();
    },
    /**
     * Ordering requires a SATELLITE cafeteria. Warn only: an unknown result (request
     * failure) must not raise a false alarm.
     */
    async checkSatelliteEligibility(establishmentId) {
      if (!establishmentId) {
        this.satelliteWarning = false;
        return;
      }
      const canOrder = await establishmentCanOrder(establishmentId);
      this.satelliteWarning = canOrder === false;
    },
    onProductionEstablishmentsLoaded(establishments) {
      this.noProductionEstablishment = (establishments || []).length === 0;
    },
    /**
     * A satellite cafeteria declares its production cafeteria: use it to prefill
     * the production establishment instead of asking the user twice.
     */
    async resolveProductionEstablishment(satelliteEstablishmentId) {
      this.productionResolvedFromCafeteria = null;
      if (!satelliteEstablishmentId) return;

      const satellite = await findSatelliteCafeteriaWithProduction(
        satelliteEstablishmentId
      );
      if (!satellite) return;

      this.order.destinationCafeteria = {
        type: "cafeterias",
        id: satellite.id,
      };
      this.order.sourceCafeteria = {
        type: "cafeterias",
        id: satellite.productionCafeteria.id,
      };
      this.productionResolvedFromCafeteria = satellite.productionCafeteria.name;

      const productionEstablishment = satellite.productionCafeteria.establishment;
      if (productionEstablishment && !this.order.sourceEstablishment.id) {
        this.order.sourceEstablishment.id = productionEstablishment.id;
      }
    },
    onFormChanged() {
      this.apiValidationErrors = {};
      this.$emit("onFormChanged", this.order);
    },
    handleSubmit() {
      this.alignWeekEnd();
      const payload = cloneDeep(this.order);
      this.$emit("submit", payload);
    },
  },
};
</script>
